import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { useAppNavigate } from '~/hooks/useAppNavigate';
import { ROLES } from '@tempus/trials-types';
import { NavigationTop } from '@tcl-boron-prefabs/navigation-top';

import TempusNextLogo from '~/assets/TempusNextLogo';
import { config } from '~/config';
import { canViewProviderReporting } from '~/utils/toggleHelpers';
import { useFeatureEnabled } from '~/components/_core/FeatureGate/FeatureGate';
import { FEATURE_FLAG } from '~/constants/feature-flags';
import { useDispatch, useSelector } from '~/store';
import { creators as siteCreators } from '~/store/site';
import { canAccessCohortBuilder } from '~/routes/builder/builderPermissions';
import { canAccessProgramManagement } from '~/routes/programManagement/programManagementPermissions';
import paths, {
  getAllPatientsPageUrl,
  getBuilderNavTabForNavigation,
  getBuilderPageUrl,
  getCarePathwaysPageUrl,
  getMatchReviewPageUrl,
  getScreenedPathwaysUrl,
  getScreenedPatientsUrl,
  getSiteDocumentsSiteUrl,
  getTempusNursePageUrl,
  getTimePrescreeningPageUrl,
  getProgramManagementPageUrl,
} from '~/routes/paths';

import {
  getAvatarConfig,
  getBaseButtonConfig,
  getHelpButtonConfig,
  getTrialsButtonConfig,
  getPathwaysOrBuilderButtonConfig,
  getTopNavSectionForNavigation,
} from './Navigation.helpers';
import ImpersonationBanner from './ImpersonationBanner';
import { LinkAppSwitcher } from '@tempus/t-shared/ui/components/LinkAppSwitcher/LinkAppSwitcher';
import { useElementWidth } from '~/components/_common/PageLayout/PageLayout';

import { useStyles } from './Navigation.styles';

/** Below this navbar width (px), hide standalone `TempusNextLogo` and use `NavigationTop` `showTempusLogo` instead. */
const NAVBAR_WIDTH_STANDALONE_LOGO_MIN = 1000;

const NAV_BUTTON_TEST_IDS_BY_LABEL: Record<string, string> = {
  Trials: 'topnav-trials',
  'Trial Match Review': 'topnav-match-review',
  Reporting: 'topnav-provider-reporting',
  Sites: 'topnav-sites',
  'My Sites': 'topnav-mysite',
  'All Patients': 'topnav-all-patients',
  'Tempus Nurse': 'topnav-tempus-nurse',
  'Cohort Builder': 'topnav-builder',
  'Screened Trials': 'topnav-time-screening',
  'Screened Pathways': 'topnav-time-screening',
  'TIME Prescreening': 'topnav-time-screening',
  'Next Prescreening': 'topnav-time-screening',
  'Program Management': 'topnav-program-management',
};

export const Navigation: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useAppNavigate();
  const { pathname, state, search } = useLocation();
  const topNavSection = getTopNavSectionForNavigation({ pathname, search });
  const builderNavTab = getBuilderNavTabForNavigation({ pathname, search, state });

  const effectiveRoles = useSelector(({ user }) => user.effectiveRoles);
  const pathwayFilters = useSelector(({ timeScreening }) => timeScreening.careGap.filters);
  const selectedSiteId = useSelector(({ site }) => site.selectedUserSite?.id || '');
  const trialsFilters = useSelector(({ timeScreening }) => timeScreening.trials.filters);
  const user = useSelector(({ user }) => user);
  const isCarePathwaysEnabled = useFeatureEnabled(FEATURE_FLAG.CARE_PATHWAYS);
  const canViewCarePathways = user.canViewCarePathways && isCarePathwaysEnabled;

  const { ref, width } = useElementWidth();
  const widthKnown = width > 0;
  const isMobile = widthKnown && width < 800;
  const showStandaloneNextLogo = !widthKnown || width >= NAVBAR_WIDTH_STANDALONE_LOGO_MIN;
  const navReady = user.featuresLoaded && widthKnown;

  useEffect(() => {
    if (user.loaded) {
      dispatch(siteCreators.getUserSites());
    }
  }, [dispatch, user.loaded]);

  const URLS = useMemo(
    () => ({
      allPatients: getAllPatientsPageUrl(selectedSiteId),
      carePathways: getCarePathwaysPageUrl(selectedSiteId),
      cohortBuilder: getBuilderPageUrl('care-pathway'),
      cohortBuilderTrial: getBuilderPageUrl('trial'),
      matchReview: getMatchReviewPageUrl(selectedSiteId),
      providerReporting: paths.PROVIDER_REPORTING,
      screenedPathways: getScreenedPathwaysUrl(pathwayFilters.tab),
      screenedTrials: getScreenedPatientsUrl(trialsFilters.tab),
      siteDocuments: getSiteDocumentsSiteUrl(selectedSiteId),
      tempusNurse: getTempusNursePageUrl(selectedSiteId),
      timePrescreening: getTimePrescreeningPageUrl('', '', ''), // TODO: pass in the linkageId, institutionId, and tab
      programManagement: getProgramManagementPageUrl(),
    }),
    [selectedSiteId, pathwayFilters.tab, trialsFilters.tab],
  );

  const permissions = useMemo(
    () => ({
      canImpersonate: user.canImpersonate,
      canScreenPatients: effectiveRoles.some((r) =>
        [ROLES.T_PATIENT_MATCHER, ROLES.NEXT_PRESCREENING_USER].includes(r),
      ),
      canViewAppSwitcher: config.toggles.appSwitcher === '1',
      canViewBuilder: canAccessCohortBuilder(user.features, effectiveRoles),
      canViewCarePathways,
      canViewInternalDocuments: user.canViewInternalDocuments,
      canViewPatientTracking: user.canViewPatientTracking,
      canViewProviderReporting: canViewProviderReporting(isCarePathwaysEnabled),
      canViewSiteDocuments: user.canViewSiteDocuments,
      isInternalPTUser: effectiveRoles.includes(ROLES.T_PATIENT_TRACKER_READ_INTERNAL),
      canViewProgramManagement: canAccessProgramManagement(effectiveRoles),
    }),
    [canViewCarePathways, effectiveRoles, isCarePathwaysEnabled, user],
  );

  const avatarConfig = useMemo(
    () => getAvatarConfig(user.user, dispatch, navigate, permissions.canImpersonate),
    [user.user, permissions.canImpersonate, dispatch, navigate],
  );

  const buttons = useMemo(() => {
    const pathwaysOrBuilderNav = getPathwaysOrBuilderButtonConfig(
      permissions,
      URLS,
      pathname,
      navigate,
      isMobile,
      topNavSection,
      builderNavTab,
    );

    const trialsNav = getTrialsButtonConfig(
      permissions,
      URLS,
      pathname,
      navigate,
      isMobile,
      topNavSection,
      builderNavTab,
    );

    return [
      ...getBaseButtonConfig(permissions, URLS, pathname, navigate, isMobile, topNavSection),
      ...(trialsNav ? [trialsNav] : []),
      ...(pathwaysOrBuilderNav ? [pathwaysOrBuilderNav] : []),
      getHelpButtonConfig(),
    ].sort((a, b) => {
      // Sort ButtonsWithMenu and ButtonWithoutMenu to intended order.
      // NavigationTop will always render IconButtons last regardless.
      const idSortOrder = [
        'trials',
        'all-patients',
        'tempus-nurse',
        'screened-trials',
        'match-review',
        'care-pathways',
        'screened-pathways',
        'cohort-builder',
        'program-management',
        'reporting',
        'sites',
        'my-site',
        'help',
      ];
      return idSortOrder.indexOf(a.id) - idSortOrder.indexOf(b.id);
    });
  }, [permissions, URLS, pathname, navigate, isMobile, topNavSection, builderNavTab]);

  /** Remount when button set changes so NavigationTop menu state stays aligned (avoids undefined.isOpen). */
  const navigationButtonsKey = useMemo(() => buttons.map((button) => button.id).join(','), [buttons]);

  useEffect(() => {
    if (!ref.current || !navReady) return;

    const allButtons = ref.current.querySelectorAll<HTMLButtonElement>('[data-analytics="navigation-top"] button');
    allButtons.forEach((btn) => btn.removeAttribute('data-nav-active'));

    allButtons.forEach((btn) => {
      const ariaLabel = btn.getAttribute('aria-label') || '';
      const dataTestId = NAV_BUTTON_TEST_IDS_BY_LABEL[ariaLabel];

      if (dataTestId) {
        btn.setAttribute('data-testid', dataTestId);
      }
    });

    if (!isMobile) return;

    buttons.forEach((navButton) => {
      // Extend button props with ariaLabel and isActive for mobile navigation styles
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const props = navButton.buttonProps as any;
      if (props.ariaLabel && props.isActive) {
        const btn = ref.current!.querySelector<HTMLButtonElement>(`button[aria-label="${props.ariaLabel}"]`);
        btn?.setAttribute('data-nav-active', 'true');
      }
    });
  }, [buttons, isMobile, navReady, ref]);

  return (
    <section className={classes.root}>
      <ImpersonationBanner />
      <div ref={ref} className={classes.navbar}>
        {showStandaloneNextLogo ? (
          <div className={classes.logo} data-testid="logo">
            <TempusNextLogo />
          </div>
        ) : null}
        <div className={classes.navTopOverrides}>
          {navReady ? (
            <NavigationTop
              key={navigationButtonsKey}
              avatar={avatarConfig}
              buttons={buttons}
              showTempusLogo={!showStandaloneNextLogo}
            />
          ) : null}
        </div>
        <div className={classes.appSwitcherContainer} data-testid="app-switcher">
          <LinkAppSwitcher appName="Next Platform" providerAccountServiceURL={config.providerAccountServiceRoot} />
        </div>
      </div>
    </section>
  );
};
